import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Row,
  Container,
  Spinner,
  Badge,
  Button,
} from "reactstrap";
import { getSingleJob } from "../../../../api/jobs";
import {
  apiErrorHandler,
  getCompanyLogo,
  getSalaryAmount,
  titleCase,
} from "../../../../helpers/common";
import { useSelector, useDispatch } from "react-redux";
import userImage6 from "../../../../assets/images/avatar.png";
import ConfirmationModal from "../../../../helpers/Common/ConfirmationModal";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import moment from "moment";

const JobDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pulling job arrays from your Redux store layers
  const { activeJobs = [], employerJobs = [] } = useSelector(
    (state) => state.jobs || state.employer || {},
  );

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        setLoading(true);

        const res = await getSingleJob(id);

        if (res?.data?.success) {
          setJob(res.data.data);
        } else {
          setJob(null);
        }
      } catch (err) {
        console.log(err);
        apiErrorHandler(err);
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchJob();
    }
  }, [id]);

  const getList = (list) => {
    if (!list) return null;

    const listArr = list.split("\n").filter((item) => item.trim() !== "");

    return listArr.map((item, index) => (
      <li key={index}>
        <i className="uil"></i> {item}
      </li>
    ));
  };

  const getKeywords = (list) => {
    if (!list) return null;

    return list.split(",").map((item, index) => (
      <span
        key={index}
        className="text-light text-uppercase badge bg-primary text-wrap lh-sm text-break me-1 mb-1"
      >
        {item.trim()}
      </span>
    ));
  };

  // Helper utility functions for custom sidebar design mappings
  const getJobExperiences = (detailsObj) => {
    if (!detailsObj) return "Not Available";
    return detailsObj.minimumExperienceRequired ||
      detailsObj.maximumExperienceRequired
      ? `${detailsObj.minimumExperienceRequired || 0} - ${detailsObj.maximumExperienceRequired || 0
      } Yrs`
      : "Not Available";
  };
  const isValidURL = (string) => {
    try {
      return Boolean(new URL(string));
    } catch (e) {
      return false;
    }
  };

  const details = job || {};
  const companyDetails = job?.company || {
    name: job?.companyName || "N/A",
    logo: job?.companyLogo,
    establishmentYear: "N/A",

    website_url: null,
    map_location: null,
  };
  // Place this right before your return statement
  const locationName =
    job?.location?.state && job?.location?.name
      ? `${job.location.state}, ${job.location.name}`
      : "India";
  return (
    <div
      className="page-content py-4 bg-light mt-4"
      style={{ minHeight: "100vh" }}
    >
      <Container>
        {/* Navigation Back Header Link */}
        <Row className="mb-3">
          <Col>
            <Button
              color="link"
              className="text-decoration-none p-0 text-primary"
              onClick={() => navigate(-1)}
            >
              <i className="uil uil-arrow-left me-1"></i> Back to Managed Jobs
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Main Context Grid Segment */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-4 mt-4">
                <div>
                  <Row>
                    <Col md={8}>
                      <h4 className="mb-1">{job?.jobTitle}</h4>

                      <ul className="list-inline text-muted mb-0">
                        <li className="list-inline-item">
                          <i className="mdi mdi-account"></i>{" "}
                          {job?.totalNumberOfVacancies || 0}{" "}
                          {job?.totalNumberOfVacancies > 1
                            ? "Vacancies"
                            : "Vacancy"}
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </div>

                <div className="mt-4">
                  <Row className="g-3 mt-1">
                    <Col lg={3} md={6}>
                      <div className="border rounded p-3 shadow-sm h-100">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "42px", height: "42px" }}
                          >
                            <i
                              className="uil uil-briefcase-alt text-primary"
                              style={{ fontSize: "20px" }}
                            ></i>
                          </div>
                          <p className="text-muted mb-0 fs-13">Experience</p>
                        </div>

                        <h6 className="fw-semibold mb-0">
                          {job?.minimumExperienceRequired || "Not Available"}
                          {job?.maximumExperienceRequired &&
                            ` - ${job.maximumExperienceRequired}`}
                        </h6>
                      </div>
                    </Col>

                    <Col lg={3} md={6}>
                      <div className="border rounded p-3 shadow-sm h-100">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "42px", height: "42px" }}
                          >
                            <i
                              className="uil uil-user-square text-success"
                              style={{ fontSize: "20px" }}
                            ></i>
                          </div>
                          <p className="text-muted mb-0 fs-13">Position</p>
                        </div>

                        <h6 className="fw-semibold mb-0">
                          {job?.jobTitle || "Not Available"}
                        </h6>
                      </div>
                    </Col>

                    <Col lg={3} md={6}>
                      <div className="border rounded p-3 shadow-sm h-100">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "42px", height: "42px" }}
                          >
                            <i
                              className="uil uil-map-marker text-danger"
                              style={{ fontSize: "20px" }}
                            ></i>
                          </div>
                          <p className="text-muted mb-0 fs-13">Location</p>
                        </div>

                        <h6 className="fw-semibold mb-0">
                          {locationName || "Not Available"}
                        </h6>
                      </div>
                    </Col>

                    <Col lg={3} md={6}>
                      <div className="border rounded p-3 shadow-sm h-100">
                        <div className="d-flex align-items-center mb-2">
                          <div
                            className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{ width: "42px", height: "42px" }}
                          >
                            <i
                              className="uil uil-money-bill text-warning"
                              style={{ fontSize: "20px" }}
                            ></i>
                          </div>
                          <p className="text-muted mb-0 fs-13">Offer Salary</p>
                        </div>

                        <h6 className="fw-semibold mb-0">
                          ₹{job?.maximumSalaryPerMonth || "Not Disclosed"} /
                          Month
                        </h6>
                      </div>
                    </Col>
                  </Row>
                </div>

                {job?.description && (
                  <div className="mt-4">
                    <h5 className="mb-3">Job Description</h5>

                    <div className="job-detail-desc">
                      <p className="text-muted mb-0">{job.description}</p>
                    </div>
                  </div>
                )}

                {job?.jobResponsibilities && (
                  <div className="mt-4">
                    <h5 className="mb-3 d-flex align-items-center">
                      <Icon
                        icon="mdi:clipboard-check-outline"
                        className="me-2 text-primary"
                        fontSize={22}
                      />
                      Key Job Responsibilities
                    </h5>

                    <div
                      className="job-detail-desc mt-2 text-muted"
                      dangerouslySetInnerHTML={{
                        __html: job.jobResponsibilities || "",
                      }}
                    />
                  </div>
                )}

                {job?.qualificationsRequired && (
                  <div className="mt-4">
                    <h5 className="mb-3 d-flex align-items-center">
                      <Icon
                        icon="mdi:school-outline"
                        className="me-2 text-success"
                        fontSize={22}
                      />
                      Qualification
                    </h5>

                    <div className="job-detail-desc mt-2">
                      <ul className="job-detail-list list-unstyled mb-0 text-muted">
                        {getList(job.qualificationsRequired)}
                      </ul>
                    </div>
                  </div>
                )}

                {job?.skillsAndExperience && (
                  <div className="mt-4">
                    <h5 className="mb-3 d-flex align-items-center">
                      <Icon
                        icon="mdi:briefcase-account-outline"
                        className="me-2 text-warning"
                        fontSize={22}
                      />
                      Skill & Experience
                    </h5>

                    <div className="job-detail-desc mt-2">
                      <ul className="job-detail-list list-unstyled mb-0 text-muted">
                        {getList(job.skillsAndExperience)}
                      </ul>
                    </div>
                  </div>
                )}

                {job?.searchKeywords && (
                  <div className="mt-4 d-flex flex-wrap align-items-start gap-1">
                    {getKeywords(job.searchKeywords)}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Updated Sidebar Design Segment */}
          <Col lg={4}>
            <div className="side-bar ms-lg-4">
              <Card className="company-profile border-0 shadow-lg rounded-4">
                <CardBody className="p-4 p-lg-5">
                  <div className="text-center">
                    <div
                      className="mx-auto d-flex align-items-center justify-content-center bg-white border rounded-4 shadow-sm overflow-hidden"
                      style={{
                        width: "170px",
                        height: "170px",
                        borderRadius: "25px",
                      }}
                    >
                      <img
                        src={getCompanyLogo(companyDetails?.logo)}
                        alt={companyDetails?.name}
                        className="img-fluid"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",

                          padding: "10px",
                        }}
                      />
                    </div>

                    <div className="mt-4">
                      <h5 className="fw-bold mb-1">{companyDetails?.name}</h5>

                      <span className="badge bg-soft-primary text-primary px-3 py-2 rounded-pill">
                        Since {companyDetails?.establishmentYear || "N/A"}
                      </span>
                    </div>
                  </div>
                  <ul className="list-unstyled mt-4">
                    {companyDetails?.website_url && (
                      <li className="mt-3">
                        <div className="d-flex">
                          <i className="uil uil-globe text-primary fs-4"></i>
                          <div className="ms-3">
                            <h6 className="fs-14 mb-2">Website</h6>
                            <a
                              className="primary-link text-muted fs-14 mb-0"
                              href={`${companyDetails?.website_url}`}
                            >
                              {companyDetails?.website_url}
                            </a>
                          </div>
                        </div>
                      </li>
                    )}
                    <li className="mt-3">
                      <div className="d-flex">
                        <i className="uil uil-map-marker text-primary fs-4"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Location</h6>
                          <p className="text-muted mb-0">
                            {details?.location?.name || details?.location
                              ? `${details?.location?.state || ""}, ${details?.location?.name || details?.location
                              }`
                              : "India"}
                          </p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </CardBody>
              </Card>

              <Card className="job-overview mt-5">
                <CardBody className="p-4">
                  <h6 className="fs-17">Job Overview</h6>
                  <ul className="list-unstyled mt-4 mb-0">
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-file-alt icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Job Title</h6>
                          <p className="text-muted mb-0">{details.jobTitle}</p>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-briefcase icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Job Vacancies</h6>
                          <p className="text-muted mb-0">
                            {details.totalNumberOfVacancies}
                          </p>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-star-half-alt icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Experience</h6>
                          <p className="text-muted mb-0">
                            {getJobExperiences(details)}
                          </p>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-location-point icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Location</h6>
                          <p className="text-muted mb-0">
                            {details?.location?.name || details?.location
                              ? `${details?.location?.state || ""}, ${details?.location?.name || details?.location
                              }`
                              : "India"}
                          </p>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-usd-circle icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Salary</h6>
                          <p className="text-muted mb-0">
                            {details?.maximumSalaryPerMonth
                              ? `₹${details.maximumSalaryPerMonth} / Month`
                              : "Not Disclosed"}
                          </p>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="d-flex mt-4">
                        <i className="uil uil-history icon bg-soft-primary"></i>
                        <div className="ms-3">
                          <h6 className="fs-14 mb-2">Date Posted</h6>
                          <p className="text-muted mb-0">
                            {moment(details?.createdAt).format("MMM DD, YYYY")}
                          </p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </CardBody>
              </Card>

              {companyDetails?.map_location &&
                isValidURL(companyDetails?.map_location) ? (
                <div className="mt-4">
                  <h6 className="fs-16 mb-3">Job location</h6>
                  <iframe
                    src={companyDetails?.map_location}
                    title="title"
                    style={{ width: `100%`, height: `250px`, border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                  ></iframe>
                </div>
              ) : null}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default JobDetail;
